import posthog from 'posthog-js';
import { IS_APP_BUILD } from './buildTarget';

// Single PostHog project for the whole product. Until 2026-07-13 an inline
// snippet in index.html initialized a SECOND project key on every load,
// splitting the data across two unmergeable projects — see
// diseno_offline_atlas_2026-07-13.md §i. This key is the surviving project;
// the static marketing pages now load a minimal snippet pointing at it too.
const POSTHOG_KEY = 'phc_do7in6x4n55wSEmwcyZ2d4AY108RHO0nXXwxdeD89Nx';

let initialized = false;

// post-t7-2b (A2, #617): LAS RUTAS DONDE LA GRABACION DE SESIONES TAPA TODO EL TEXTO, porque ahi se ven datos de
// terceros: la pagina del grupo (`/groups/:groupId`, con su lista de alumnos), el panel "Avance del grupo"
// (`/groups/:groupId/avance`, con su chat, que se pinta en un portal fuera de la pagina) y todo el admin (`/admin` y
// `/admin/...`), sin distinguir mayusculas como el router. El banco del profe (`/groups/:groupId/bank/...`) y las demas rutas se graban como antes.
const RUTA_DEL_GRUPO = /^\/groups\/[^/]+(\/avance)?\/?$/i;
const RUTA_DEL_ADMIN = /^\/admin(\/.*)?$/i;

export function rutaConTextoTapado(pathname) {
  if (typeof pathname !== 'string') return false;
  return RUTA_DEL_GRUPO.test(pathname) || RUTA_DEL_ADMIN.test(pathname);
}

// En posthog-js 1.378.1 (`lazy-recorder.js`, el rrweb empaquetado) `maskTextFn` solo se llama sobre el texto que
// `maskTextSelector` ya marco, y recibe el texto y su elemento; por eso el selector abarca TODO y la decision es por
// la ruta del momento en que se graba el texto: asteriscos del mismo largo (como el tapado propio de rrweb, que
// respeta los espacios) en las rutas de arriba, el texto tal cual en las demas.
function taparTextoSegunLaRuta(text) {
  if (typeof text !== 'string') return text;
  return rutaConTextoTapado(window.location.pathname) ? text.replace(/\S/g, '*') : text;
}

// PLAN B (post-t7-2b, A2, #617), medido en Chrome con el arnes A2: `maskTextFn` tapa el TEXTO pero no los
// ATRIBUTOS (rrweb los graba tal cual, y esta version no trae forma de taparlos), y el panel lleva nombres de alumnos
// en `title`. Por eso, en las rutas de arriba la grabacion se DETIENE al entrar y se reanuda al salir; el tapado de
// texto se queda como segunda capa.
// - Al entrar se detiene en el acto, dentro del propio `pushState` / `replaceState` / `popstate`: el router cambia
//   la direccion ANTES de pintar la pantalla nueva, asi que nada de ella llega a grabarse.
// - Al salir se reanuda tras `PAUSA_AL_SALIR_MS`, para que la pantalla nueva ya haya reemplazado a la tapada (al
//   reanudar se toma una foto completa de la pagina). Si en la pausa vuelve a una ruta tapada, no se reanuda.
// - `startSessionRecording()` va sin argumentos: no se salta el muestreo ni las reglas del proyecto en PostHog.
const PAUSA_AL_SALIR_MS = 1000;
let detenidaPorRuta = false;
let reanudar = null;

function ajustarGrabacionALaRuta() {
  try {
    if (rutaConTextoTapado(window.location.pathname)) {
      clearTimeout(reanudar);
      reanudar = null;
      detenidaPorRuta = true;
      posthog.stopSessionRecording();
      return;
    }
    if (!detenidaPorRuta || reanudar) return;
    reanudar = setTimeout(() => {
      reanudar = null;
      if (rutaConTextoTapado(window.location.pathname)) return;
      detenidaPorRuta = false;
      posthog.startSessionRecording();
    }, PAUSA_AL_SALIR_MS);
  } catch {}
}

// post-t7-2b (A2, #617, remate): LOS EVENTOS TAMPOCO LLEVAN TEXTO DE LA PANTALLA en las rutas tapadas. Medido en
// Chrome (arnes A2): el autocapture mandaba el texto del boton tocado (un titulo de chat con nombres de alumnos).
// `before_send` de posthog-js 1.378.1 (`BeforeSendFn = (cr: CaptureResult | null) => CaptureResult | null`,
// `@posthog/types/dist/capture.d.ts:77`; `before_send?: BeforeSendFn | BeforeSendFn[]`, `posthog-config.d.ts:1282`)
// corre sobre CADA evento antes de enviarlo, y devolver null lo descarta. En las rutas tapadas: la familia del
// autocapture se descarta entera y a cualquier otro evento se le quitan las propiedades con texto o elementos de la
// pagina. Fuera de esas rutas devuelve el MISMO evento, sin tocarlo.
const EVENTOS_DE_LA_PANTALLA = new Set(['$autocapture', '$rageclick', '$dead_click', '$copy_autocapture', '$$heatmap']);
const PROPIEDADES_DE_LA_PANTALLA = new Set([
  '$el_text', '$elements', '$elements_chain', '$selected_content', '$external_click_url',
]);

export function antesDeEnviar(evento) {
  if (!evento || !rutaConTextoTapado(window.location.pathname)) return evento;
  if (EVENTOS_DE_LA_PANTALLA.has(evento.event)) return null;
  const props = evento.properties;
  if (!props || !Object.keys(props).some((k) => PROPIEDADES_DE_LA_PANTALLA.has(k))) return evento;
  const limpias = {};
  for (const [k, v] of Object.entries(props)) if (!PROPIEDADES_DE_LA_PANTALLA.has(k)) limpias[k] = v;
  return { ...evento, properties: limpias };
}

function vigilarLaRuta() {
  for (const metodo of ['pushState', 'replaceState']) {
    const original = window.history[metodo];
    window.history[metodo] = function vigilado(...args) {
      const resultado = original.apply(this, args);
      ajustarGrabacionALaRuta();
      return resultado;
    };
  }
  window.addEventListener('popstate', ajustarGrabacionALaRuta);
}

export function initAnalytics() {
  // The Capacitor app build ships without product analytics (decision F4.2a:
  // "the app build does not track"). Gating the init here — the single runtime
  // choke point for PostHog — leaves the wrappers below as safe no-ops in the app
  // (PostHog is never initialized, so nothing loads, queues, or hits the network).
  // The web build is unaffected: IS_APP_BUILD is a compile-time false, so this
  // guard is stripped by the minifier and behavior is byte-identical.
  if (IS_APP_BUILD) return;
  if (initialized) return;
  try {
    // post-t7-2b (A2, #617, plan B): si la pagina arranca en una ruta tapada, la grabacion nace detenida.
    detenidaPorRuta = rutaConTextoTapado(window.location.pathname);
    posthog.init(POSTHOG_KEY, {
      api_host: 'https://us.i.posthog.com',
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage',
      // Carried over from the removed inline snippet so session replay keeps
      // working (now on this project) and person handling stays identical.
      // 'identified_only' is also posthog-js's own default — explicit because
      // the inline snippet set it explicitly.
      person_profiles: 'identified_only',
      disable_session_recording: detenidaPorRuta,
      before_send: antesDeEnviar,
      session_recording: {
        recordCrossOriginIframes: true,
        capturePerformance: false,
        // post-t7-2b (A2, #617): lo que se escribe en los campos se graba oculto (explicito; es el valor de
        // posthog-js por omision) y el texto de la pantalla se tapa en las rutas con datos de terceros.
        maskAllInputs: true,
        maskTextSelector: '*',
        maskTextFn: taparTextoSegunLaRuta,
      },
    });
    initialized = true;
    vigilarLaRuta();
  } catch (e) {
    console.warn('Analytics init failed:', e);
  }
}

export function identifyUser(userId, properties = {}) {
  // No-op in the app build (PostHog never initialized). The try/catch below
  // already tolerated an uninitialized PostHog; this makes it a true no-op.
  if (IS_APP_BUILD) return;
  try {
    posthog.identify(userId, properties);
  } catch {}
}

export function resetAnalytics() {
  if (IS_APP_BUILD) return;
  try {
    posthog.reset();
  } catch {}
}

export function trackEvent(eventName, properties = {}) {
  if (IS_APP_BUILD) return;
  try {
    posthog.capture(eventName, properties);
  } catch {}
}
